/** @jsx jsx */

import { Flex, FormGroup, Link, Paragraph } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { FormikError } from "@/components/Formik";
import { FormikCheckbox } from "@/components/Formik/FormikCheckbox";
import { If } from "@/components/If";
import { UserContext } from "@/components/User";
import { IJobRequestConsent } from "@/data/job-requests";
import { EOriginSystemOwner } from "@/data/job-requests/types/JobRequestOrigin";
import { SummaryFormValues } from "@/views/job-applications/apply/_summary/ApplySummaryForm";

import { ApplyConsentModal } from "./ApplyConsentModal";

interface ApplyConsentSectionProps {
  formik: FormikProps<SummaryFormValues>;
  consent?: IJobRequestConsent | null;
  jobRequestOrigin?: EOriginSystemOwner | null;
}

export const ApplyConsentSection: FC<ApplyConsentSectionProps> = ({ consent, formik, jobRequestOrigin }) => {
  const { t } = useTranslation("session");

  const { contextCompany } = useContext(UserContext);

  const modalState = useDialogState({ animated: true });

  const isVmsOrigin = jobRequestOrigin === EOriginSystemOwner.VMS;

  if (!consent) {
    return null;
  }

  const consentDeclaration = isVmsOrigin
    ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.CONSENT_DECLARATION_VMS")
    : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.CONSENT_DECLARATION_NOVA");

  const checkboxConsentText = isVmsOrigin
    ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.CHECKBOX_CONSENT_TEXT_VMS")
    : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.CHECKBOX_CONSENT_TEXT_NOVA");

  const readPolicyButtonText = isVmsOrigin
    ? t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.BUTTON_OPENER_CONSENT_VMS")
    : t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.BUTTON_OPENER_CONSENT_NOVA");

  return (
    <React.Fragment>
      <Flex flexDirection="column">
        <Flex flexDirection="column">
          <FormGroup error={<FormikError name="consentApproved" />}>
            <Flex flexDirection="row" alignItems="center" gap="xs">
              <FormikCheckbox name="consentApproved" checked={formik.values.consentApproved} />

              <Paragraph size="p-md">
                <Paragraph>
                  {consentDeclaration}
                  <br />
                </Paragraph>

                <Paragraph>
                  {checkboxConsentText}
                  &nbsp;
                </Paragraph>

                <Link as={DialogDisclosure} {...modalState} sx={{ verticalAlign: "baseline" }}>
                  {readPolicyButtonText}
                </Link>

                <Paragraph>
                  . <Paragraph color="r500">*</Paragraph>
                </Paragraph>
              </Paragraph>
            </Flex>
          </FormGroup>

          <If predicate={Boolean(contextCompany)}>
            <FormGroup error={<FormikError name="completeProfileConsentApproved" />}>
              <Flex mt="sm" flexDirection="row" gap="xs" alignItems="center">
                <FormikCheckbox
                  name="completeProfileConsentApproved"
                  checked={formik.values.completeProfileConsentApproved}
                />
                <Paragraph size="p-md">
                  {t("JOB_REQUESTS.APPLY.CONSULTANT_PROFILE_FORM.CHECKBOX_COMPANY_PROFILE")}
                  <Paragraph color="r500">*</Paragraph>
                </Paragraph>
              </Flex>
            </FormGroup>
          </If>
        </Flex>
      </Flex>

      <ApplyConsentModal dialogState={modalState} consent={consent} />
    </React.Fragment>
  );
};
